import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql/gql';
import { useAuth0 } from '../auth/AuthProvider';
import { graphqlRequest, shouldRetryQuery } from '../utils/graphqlClient';
import { MyProfileQuery } from '../gql/graphql';
import { normalizeCertificateLevel } from '../constants/diveCertification';
import {
  getProfileReadiness,
  PROFILE_FIELD_LABELS,
  ProfileCompletenessData,
  ProfileSection,
} from '../pages/Profile/utilities/profileCompleteness';
import { profileQueryKeys } from './useProfileMutationFeedback';

const getMyProfileQuery = graphql(/* GraphQL */ `
  query myProfile {
    myProfile {
      user {
        userId
        auth0Id
        email
        fullName
        gender
        phone
        dateOfBirth
        specialRequest
      }
      travelDocument {
        documentId
        countryOfIssuance
        passportNumber
        expiryDate
      }
      emergencyContact {
        contactId
        fullName
        phone
      }
      diveCertificate {
        certificateId
        certificateLevel
        certifyingAgency
        certificateNumber
      }
      diveEquipment {
        equipmentId
        bcd
        wetsuit
        fins
      }
      diveInsurance {
        insuranceId
        policyNumber
      }
      readinessPercent
      basicPersonalInfoComplete
      travelDocumentsComplete
      diveCertificateComplete
      missingFields
      nextSection
    }
  }
`);

type ReadinessSection = Exclude<ProfileSection, 'diveEquipment'>;

const isReadinessSection = (
  value: string | null | undefined,
): value is ReadinessSection =>
  value === 'basicPersonalInfo' ||
  value === 'travelDocuments' ||
  value === 'diveCertificate';

export const mapMyProfileToCompletenessData = (
  myProfile: MyProfileQuery['myProfile'] | null | undefined,
): ProfileCompletenessData => ({
  fullName: myProfile?.user?.fullName ?? '',
  gender: myProfile?.user?.gender ?? '',
  email: myProfile?.user?.email ?? '',
  phone: myProfile?.user?.phone ?? '',
  countryOfIssuance: myProfile?.travelDocument?.countryOfIssuance ?? '',
  passportNumber: myProfile?.travelDocument?.passportNumber ?? '',
  expiryDate: myProfile?.travelDocument?.expiryDate
    ? String(myProfile.travelDocument.expiryDate)
    : '',
  emergencyContactName: myProfile?.emergencyContact?.fullName ?? '',
  emergencyContactPhone: myProfile?.emergencyContact?.phone ?? '',
  certificateLevel: normalizeCertificateLevel(
    myProfile?.diveCertificate?.certificateLevel,
  ),
  certifyingAgency: myProfile?.diveCertificate?.certifyingAgency ?? '',
  certificateNumber: myProfile?.diveCertificate?.certificateNumber ?? '',
});

const useMyProfile = (enabled: boolean = true) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const { data, error, isLoading } = useQuery<MyProfileQuery>({
    queryKey: profileQueryKeys.myProfile,
    queryFn: async () =>
      graphqlRequest<MyProfileQuery>(
        getMyProfileQuery,
        {},
        enabled && isAuthenticated ? getAccessTokenSilently : undefined,
      ),
    enabled: enabled && isAuthenticated,
    retry: shouldRetryQuery,
  });

  const myProfile = data?.myProfile;
  const completenessData = mapMyProfileToCompletenessData(myProfile);
  const localReadiness = getProfileReadiness(completenessData);
  const nextSection = isReadinessSection(myProfile?.nextSection)
    ? myProfile.nextSection
    : localReadiness.nextSection;

  return {
    myProfile,
    completenessData,
    readinessPercent:
      myProfile?.readinessPercent ?? localReadiness.readinessPercent,
    missingFields:
      myProfile?.missingFields ??
      localReadiness.missingFields.map(
        field => PROFILE_FIELD_LABELS[field.fieldName],
      ),
    nextSection,
    isLoading,
    isError: !!error,
    error,
  };
};

export default useMyProfile;
